/* eslint-disable @next/next/no-img-element */
import { useEffect, useState, Fragment } from "react";
import { motion } from "framer-motion";
import clsx from "clsx";

import { Transition, Listbox } from "@headlessui/react";
import {
  ExclamationCircleIcon,
  SelectorIcon,
  CheckIcon,
} from "@heroicons/react/outline";

import { IoMdClose } from "react-icons/io";
import { FaCheckCircle } from "react-icons/fa";

const positions = ["FOR", "AGAINST", "UNDECIDED", "NO STANCE"];

const TarrantCountyComment = ({ saveSpeaker, topic, tarrantSuccess }) => {
  const [canSave, setCanSave] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const [user, setUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    addressPrimary: "",
    addressSecondary: "",
    city: "",
    state: "TX",
    zipCode: "",
    attending: false,
    speaking: false,
    comment: "",
    representingOrg: "",
    isResident: true,
    subject: "",
    position: positions[0],
  });

  useEffect(() => {
    const requiredFields = [
      "firstName",
      "lastName",
      "phoneNumber",
      "addressPrimary",
      "city",
      "state",
      "zipCode",
      "comment",
    ];

    const isValid = requiredFields.every((field) => {
      const value = user[field];
      return typeof value === "string" && value.trim() !== "";
    });

    setCanSave(isValid);
  }, [user]);

  function change(key, value) {
    setUser({
      ...user,
      [key]: value,
    });
  }

  function handlePosition(value) {
    setUser({
      ...user,
      position: value,
    });
  }

  function handleSubmit() {
    const groupName = topic?.agendaGroup?.parentGroup
      ? `${topic.agendaGroup.parentGroup.name} - ${topic.agendaGroup.name}`
      : topic?.agendaGroup?.name ?? "";

    const { firstName, lastName, ...rest } = user;
    const fullName = `${firstName} ${lastName}`.trim();

    saveSpeaker(
      "submit",
      { ...rest, name: fullName },
      {
        _id: topic._id,
        verbiage: `${groupName ? groupName + " – " : ""}${topic.title}`,
      }
    );

    setUser({
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      addressPrimary: "",
      addressSecondary: "",
      city: "",
      state: "TX",
      zipCode: "",
      attending: false,
      speaking: false,
      comment: "",
      representingOrg: "",
      isResident: true,
      subject: "",
      position: positions[0],
    });
  }

  const isFieldInvalid = (field) => {
    return !user[field] || user[field].trim?.() === "";
  };

  return (
    <motion.div
      className="h-screen fixed inset-0 bg-white z-50"
      initial={{ x: "100%" }}
      animate={{ x: "0%" }}
      exit={{ x: "100%" }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex items-center justify-between border-b border-slate-400 p-4">
        <div className="flex">
          <p className="text-black font-bold ml-4 py-1.5 text-sm lg:text-base 2xl:text-xl">
            Tarrant County Commissioners Court
          </p>
        </div>
        <IoMdClose
          className="h-7 w-7 text-black cursor-pointer hover:text-slate-500 hover:rotate-180 transform duration-300"
          onClick={() => {
            saveSpeaker("cancel");
            setUser({
              name: "",
              email: "",
              phoneNumber: "",
              addressPrimary: "",
              addressSecondary: "",
              city: "",
              state: "TX",
              zipCode: "",
              attending: true,
              speaking: true,
              comment: "",
              representingOrg: "",
              isResident: true,
              subject: "",
              position: positions[0],
            });
          }}
        />
      </div>
      <div className="overflow-y-auto max-h-[calc(100vh-64px)] pb-32">
        {tarrantSuccess ? (
          <div className="w-full flex flex-col items-center mt-4">
            <div className="flex items-center">
              <FaCheckCircle className="h-5 w-5 md:h-7 md:w-7 2xl:h-8 2xl:w-8 text-blue-500 mr-4" />
              <p className="text-sm lg:text-base 2xl:text-xl font-semibold">
                Your Registration has been received.
              </p>
            </div>

            <p className="text-sm lg:text-sm 2xl:text-base">
              You will receive a confirmation email shortly.
            </p>
          </div>
        ) : (
          <div className="w-full flex flex-col items-center mt-4">
            <p className="text-black font-semibold 2xl:text-lg underline">
              Public Comment Form
            </p>
            <p className="text-black font-semibold 2xl:text-lg mt-1">
              {topic?.agendaGroup.parentGroup
                ? `${topic?.agendaGroup.parentGroup.name} - ${topic?.agendaGroup.name}`
                : topic?.agendaGroup.name}
            </p>
            {!topic.registrationTopic && (
              <p className="text-black font-semibold 2xl:text-lg mt-1">
                {topic?.title}
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-2 lg:gap-4 p-4 max-w-7xl 2xl:max-w-5xl mx-auto w-full">
              {/* FirstName */}
              <div className="">
                <label
                  htmlFor="full_name"
                  className="text-sm font-medium text-black inline-flex items-center"
                >
                  First Name{" "}
                  {isFieldInvalid("firstName") && (
                    <ExclamationCircleIcon
                      className="h-4 w-4 text-red-700 ml-2"
                      aria-hidden="true"
                    />
                  )}
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <input
                    type="text"
                    name="full_name"
                    id="firstName"
                    className="mt-1 block w-full border border-black rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm"
                    value={user.firstName}
                    onChange={(e) => {
                      //   if (!signage) {
                      change(e.target.id, e.target.value);
                      //   }
                    }}
                  />
                </div>
              </div>
              {/* Last Name */}
              <div className="">
                <label
                  htmlFor="full_name"
                  className="text-sm font-medium text-black inline-flex items-center"
                >
                  Last Name{" "}
                  {isFieldInvalid("lastName") && (
                    <ExclamationCircleIcon
                      className="h-4 w-4 text-red-700 ml-2"
                      aria-hidden="true"
                    />
                  )}
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <input
                    type="text"
                    name="full_name"
                    id="lastName"
                    className="mt-1 block w-full border border-black rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm"
                    value={user.lastName}
                    onChange={(e) => {
                      //   if (!signage) {
                      change(e.target.id, e.target.value);
                      //   }
                    }}
                  />
                </div>
              </div>
              {/* Email */}
              <div className="md:col-span-2">
                <label
                  htmlFor="full_name"
                  className="text-sm font-medium text-black inline-flex items-center"
                >
                  Email{" "}
                  {isFieldInvalid("email") && (
                    <ExclamationCircleIcon
                      className="h-4 w-4 text-red-700 ml-2"
                      aria-hidden="true"
                    />
                  )}
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <input
                    type="text"
                    name="full_name"
                    id="email"
                    className="mt-1 block w-full border border-black rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm"
                    value={user.email}
                    onChange={(e) => {
                      //   if (!signage) {
                      change(e.target.id, e.target.value);
                      //   }
                    }}
                  />
                </div>
              </div>
              {/* Phone Number */}
              <div className="">
                <label
                  htmlFor="title"
                  className="inline-flex text-sm font-medium text-black"
                >
                  Phone Number{" "}
                  {isFieldInvalid("phoneNumber") && (
                    <ExclamationCircleIcon
                      className="h-4 w-4 text-red-700 ml-2"
                      aria-hidden="true"
                    />
                  )}
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <input
                    type="text"
                    name="title"
                    id="phoneNumber"
                    className="mt-1 block w-full border border-black rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm"
                    value={user.phoneNumber}
                    onChange={(e) => {
                      //   if (!signage) {
                      change(e.target.id, e.target.value);
                      //   }
                    }}
                  />
                </div>
              </div>
              {/* Address */}
              <div className="md:col-span-2">
                <label
                  htmlFor="title"
                  className="text-sm font-medium text-black inline-flex"
                >
                  Address{" "}
                  {isFieldInvalid("addressPrimary") && (
                    <ExclamationCircleIcon
                      className="h-4 w-4 text-red-700 ml-2"
                      aria-hidden="true"
                    />
                  )}
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <input
                    type="text"
                    name="title"
                    id="addressPrimary"
                    className="mt-1 block w-full border border-black rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm"
                    value={user.addressPrimary}
                    onChange={(e) => {
                      //   if (!signage) {
                      change(e.target.id, e.target.value);
                      //   }
                    }}
                  />
                </div>
              </div>
              {/* City */}
              <div className="md:col-span-2">
                <label
                  htmlFor="title"
                  className="text-sm font-medium text-black inline-flex"
                >
                  City{" "}
                  {isFieldInvalid("city") && (
                    <ExclamationCircleIcon
                      className="h-4 w-4 text-red-700 ml-2"
                      aria-hidden="true"
                    />
                  )}
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <input
                    type="text"
                    name="title"
                    id="city"
                    className="mt-1 block w-full border border-black rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm"
                    value={user.city}
                    onChange={(e) => {
                      //   if (!signage) {
                      change(e.target.id, e.target.value);
                      //   }
                    }}
                  />
                </div>
              </div>
              {/* State */}
              {/* Zip Code */}
              <div className="">
                <label
                  htmlFor="title"
                  className="inline-flex text-sm font-medium text-black"
                >
                  Zip Code{" "}
                  {isFieldInvalid("zipCode") && (
                    <ExclamationCircleIcon
                      className="h-4 w-4 text-red-700 ml-2"
                      aria-hidden="true"
                    />
                  )}
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <input
                    type="text"
                    name="title"
                    id="zipCode"
                    className="mt-1 block w-full border border-black rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm"
                    value={user.zipCode}
                    onChange={(e) => {
                      //   if (!signage) {
                      change(e.target.id, e.target.value);
                      //   }
                    }}
                  />
                </div>
              </div>
              {/* Phone Number */}

              {/* Position */}
              <div className="md:col-span-5">
                <label
                  htmlFor="position"
                  className="block text-sm font-medium text-black"
                >
                  Position
                </label>
                <Listbox value={user.position} onChange={handlePosition}>
                  {({ open }) => (
                    <>
                      <div className="relative mt-1">
                        <Listbox.Button className="relative w-full py-2 pl-3 pr-10 text-left bg-white rounded-md border-2 border-coolGray-400 shadow-md cursor-default focus:outline-none sm:text-sm">
                          <span className="block truncate text-black">
                            {user.position}
                          </span>
                          <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                            <SelectorIcon
                              className="w-5 h-5 text-black p-0.5"
                              aria-hidden="true"
                            />
                          </span>
                        </Listbox.Button>
                        <Transition
                          show={open}
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <Listbox.Options
                            static
                            className="absolute w-full py-1 mt-1 overflow-auto text-base bg-zinc-50 rounded-md shadow-2xl max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-50 border-l border-r border-b border-gray-600"
                          >
                            {positions.map((person, personIdx) => (
                              <Listbox.Option
                                key={personIdx}
                                className={({ active }) =>
                                  `${
                                    active
                                      ? "text-white bg-coolGray-600"
                                      : "text-gray-900"
                                  }
                                    cursor-default select-none relative py-2 pl-10 pr-4`
                                }
                                value={person}
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span
                                      className={`${
                                        selected ? "font-medium" : "font-normal"
                                      } block truncate`}
                                    >
                                      {person}
                                    </span>
                                    {selected ? (
                                      <span
                                        className={`${
                                          active
                                            ? "text-white"
                                            : "text-gray-900"
                                        }
                                          absolute inset-y-0 left-0 flex items-center pl-3`}
                                      >
                                        <CheckIcon
                                          className="w-5 h-5"
                                          aria-hidden="true"
                                        />
                                      </span>
                                    ) : null}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    </>
                  )}
                </Listbox>
              </div>
              {/* CHeckboxes */}
              {/* <div className="md:col-span-5 max-w-7xl 2xl:max-w-3xl mx-auto">
                <div className="flex items-center space-x-2 mt-6">
                  <input
                    type="checkbox"
                    checked={user.speaking}
                    className="h-4 w-4 md:h-7 md:w-7 text-zinc-800 bg-white border-2 border-black rounded focus:outline-none"
                    onClick={() => change("speaking", true)}
                  />
                  <p className="text-sm md:text-base 2xl:text-xl font-medium">
                    I wish to address the Court on the following agenda item.
                  </p>
                </div>
                <div className="flex items-center space-x-2 mt-6">
                  <input
                    type="checkbox"
                    checked={!user.speaking}
                    className="h-4 w-4 md:h-7 md:w-7 text-zinc-800 bg-white border-2 border-black rounded focus:outline-none"
                    onClick={() => change("speaking", false)}
                  />
                  <p className="text-sm md:tex-tbase 2xl:text-xl font-medium">
                    I do not wish to address the Court, but I would like to
                    submit the following opinion.
                  </p>
                </div>
              </div> */}
              {/* Comments */}
              <div className="md:col-span-5">
                <label
                  htmlFor="title"
                  className="block text-sm font-medium text-black"
                >
                  Comments
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <textarea
                    type="text"
                    name="comment"
                    id="comment"
                    rows="4"
                    className="mt-1 block w-full border border-black rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm"
                    value={user.comment}
                    onChange={(e) => {
                      //   if (!signage) {
                      change(e.target.id, e.target.value);
                      //   }
                    }}
                  ></textarea>
                </div>
              </div>
              {/* GuideLInes */}

              <p className="text-black text-justify 2xl:text-lg mt-2 md:col-span-5">
                This form is for written public comments only. Submission of
                this form does not register an individual to speak during
                Commissioners Court. Individuals who wish to speak during Court
                must click the “Register to Speak In-Person” button to complete
                the Speaker Registration Form.
              </p>

              {/* <div className="md:col-span-5">
                <p className="text-red-700 font-semibold text-justify 2xl:text-lg mt-2">
                  In submitting this Audience Participation Form you agree to
                  read and abide by the{" "}
                  <a
                    href="http://www.tarrantcountytx.gov/rulesofdecorum"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-800 underline hover:text-blue-800"
                  >
                    Tarrant County Rules of Decorum Policy
                  </a>
                  .
                </p>
              </div> */}
              {/* {canSave && (
                <div className="flex items-center space-x-2 md:col-span-5">
                  <input
                    type="checkbox"
                    checked={canSubmit}
                    className="h-4 w-4 2xl:h-6 2xl:w-6 text-zinc-800 bg-white border-2 border-black rounded focus:outline-none"
                    onClick={() => setCanSubmit(!canSubmit)}
                  />
                  <p className="text-sm 2xl:text-xl font-medium">
                    I have read and agree to abide by the Tarrant County
                    Commissioners Court Rules of Decorum.
                  </p>
                </div>
              )} */}
              {canSave && (
                <div className="md:col-span-5 flex justify-between">
                  <button
                    className={clsx(
                      "px-4 py-2 rounded-lg text-white font-semibold hover:bg-blue-400 transform duration-200 bg-blue-600"
                    )}
                    onClick={() => handleSubmit()}
                  >
                    Submit
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default TarrantCountyComment;
